import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { RSVP, Event, UnitType } from '@/types';
import { useRanks } from '@/hooks/useRanks';
import { getAvailablePrefixes, getRankAndShortName } from '@/lib/rank-utils';

export default function EditRSVPPage() {
  const { unitId: unitId, eventId, rsvpId } = useParams<{ unitId: string; eventId: string; rsvpId: string }>();
  const { currentUser } = useAuth();
  const { ranks } = useRanks();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [currentUnit, setCurrentUnit] = useState<any>(null);

  // Form state
  const [name, setName] = useState('');
  const [rank, setRank] = useState('');
  const [additionalRank, setAdditionalRank] = useState('');
  const [unitName, setUnitName] = useState('');
  const [mealChoice, setMealChoice] = useState('');
  const [courseSelections, setCourseSelections] = useState<Record<string, string>>({});
  const [dietary, setDietary] = useState<string[]>([]);
  const [allergies, setAllergies] = useState('');

  const currentUnitType = currentUnit ? unitTypes.find((ut) => ut.id === currentUnit.typeId) : undefined;
  const unitLabel = currentUnitType?.entityName || 'Unit/Organization';
  const requireAffiliationInfo = event?.requirePrefixRankUnitInfo ?? true;

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setUnitTypes(data.unitTypes || []);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  // Fetch current unit
  useEffect(() => {
    const fetchCurrentUnit = async () => {
      if (!unitId) return;
      
      try {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          setCurrentUnit({
            id: unitDoc.id,
            ...unitDoc.data()
          });
        }
      } catch (error) {
        console.error('Error fetching current unit:', error);
      }
    };

    fetchCurrentUnit();
  }, [unitId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId || !rsvpId) {
        showError('Invalid RSVP link');
        setLoading(false);
        return;
      }

      try {
        // Fetch RSVP
        const rsvpDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
        if (!rsvpDoc.exists()) {
          showError('RSVP not found');
          setLoading(false);
          return;
        }

        const rsvpData = rsvpDoc.data() as RSVP;
        
        // Check if user is authorized
        if (rsvpData.userId !== currentUser?.uid) {
          showError('You are not authorized to edit this RSVP');
          navigate('/login');
          return;
        }

        // Fetch event
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (!eventDoc.exists()) {
          showError('Event not found');
          setLoading(false);
          return;
        }

        const eventData = eventDoc.data();
        const eventDate = eventData.date?.toDate() || new Date();
        const eventObj: Event = {
          id: eventDoc.id,
          unitId,
          name: eventData.name,
          date: eventDate,
          location: eventData.location,
          costPerPerson: eventData.costPerPerson,
          questionnaire: eventData.questionnaire || [],
          courses: eventData.courses || [],
          maxCapacity: eventData.maxCapacity,
          currentAttendees: eventData.currentAttendees || 0,
          waitlistEnabled: eventData.waitlistEnabled || false,
          waitlist: eventData.waitlist || [],
          rsvpDeadline: eventData.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: eventData.paymentDeadline?.toDate() || eventDate,
          eventStatus: eventData.eventStatus,
          dresscode: eventData.dresscode,
          customInstructions: eventData.customInstructions,
          isPublic: eventData.isPublic ?? false,
          requirePrefixRankUnitInfo: eventData.requirePrefixRankUnitInfo ?? true,
          createdBy: eventData.createdBy,
          createdAt: eventData.createdAt?.toDate() || new Date(),
        };

        // Check if past deadline
        if (new Date() > eventObj.rsvpDeadline) {
          showError('The RSVP deadline has passed. You can no longer edit your registration.');
          setLoading(false);
          return;
        }

        if (eventObj.eventStatus === 'closed') {
          showError('This event is locked. RSVPs can no longer be changed.');
          setLoading(false);
          return;
        }

        setEvent(eventObj);
        setRsvp(rsvpData);
        
        // Populate form
        setName(rsvpData.name);
        setRank(rsvpData.rank);
        setAdditionalRank(rsvpData.additionalRank || '');
        setUnitName(rsvpData.unit);
        
        // Load course selections or legacy meal choice
        if (eventObj.courses && eventObj.courses.length > 0) {
          const selections: Record<string, string> = {};
          eventObj.courses.forEach((course) => {
            selections[course.id] = rsvpData.responses?.[course.name] || '';
          });
          setCourseSelections(selections);
        } else {
          setMealChoice(rsvpData.responses?.Meal || '');
        }
        
        setDietary(rsvpData.responses?.Dietary || []);
        setAllergies(rsvpData.responses?.Allergies || '');

      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Failed to load RSVP');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId, rsvpId, currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (requireAffiliationInfo) {
      if (!rank.trim()) {
        showError('Please select your prefix.');
        return;
      }
    }

    // Validate based on whether event uses courses or legacy meal choice
    if (!name.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    if (event?.courses && event.courses.length > 0) {
      // Check if all courses have selections
      const allCoursesSelected = event.courses.every(
        (course) => courseSelections[course.id]?.trim()
      );
      if (!allCoursesSelected) {
        showError('Please select an option for all courses');
        return;
      }
    } else if (!mealChoice) {
      showError('Please fill in all required fields');
      return;
    }

    if (!unitId || !eventId || !rsvpId || !rsvp) {
      showError('Invalid RSVP');
      return;
    }

    if (event?.eventStatus === 'closed') {
      showError('This event is locked. RSVPs can no longer be changed.');
      return;
    }

    setSubmitting(true);
    try {
      // Preserve existing responses (Tickets, TicketCourses, etc.) and only update fields we edit
      const responses: Record<string, any> = {
        ...(rsvp.responses || {}),
        Allergies: allergies,
      };

      // Add course selections or legacy meal choice
      if (event?.courses && event.courses.length > 0) {
        event.courses.forEach((course) => {
          responses[course.name] = courseSelections[course.id];
        });
      } else {
        responses.Meal = mealChoice;
      }

      if (dietary.length > 0) {
        responses.Dietary = dietary;
      }

      const normalizedRank = rank?.trim() || '';
      const normalizedAdditionalRank = additionalRank?.trim() || '';
      const normalizedUnitName = unitName?.trim() || '';

      const updateData: any = {
        name: name.trim(),
        responses,
        updatedAt: new Date(),
      };
      
      if (requireAffiliationInfo) {
        const { rank: resolvedRank, rankShortName } = getRankAndShortName({
          rankName: normalizedRank,
          currentUnitType,
          globalRanks: ranks,
        });

        updateData.rank = resolvedRank;
        updateData.rankShortName = rankShortName ?? null;
        updateData.unit = normalizedUnitName || null;
        // Always persist additionalRank explicitly so it can be cleared
        updateData.additionalRank = normalizedAdditionalRank || null;
      }

      await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), updateData);

      showSuccess('RSVP updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating RSVP:', error);
      showError('Failed to update RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const dietaryOptions = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Halal', 'Kosher'];

  const toggleDietary = (option: string) => {
    setDietary((prev) =>
      prev.includes(option) ? prev.filter((d) => d !== option) : [...prev, option]
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading RSVP...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event || !rsvp) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>RSVP Not Found</CardTitle>
              <CardDescription>The RSVP you're looking for doesn't exist or you don't have permission to edit it.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              ← Back to Dashboard
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Edit Your RSVP</CardTitle>
              <CardDescription>
                Update your registration details for {event.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Personal Details</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  {requireAffiliationInfo && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="rank">Prefix *</Label>
                        <Select value={rank} onValueChange={setRank}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your prefix" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailablePrefixes(event, currentUnitType).map((prefix) => (
                              <SelectItem key={prefix.id} value={prefix.name}>
                                {prefix.shortName ? `${prefix.name} (${prefix.shortName})` : prefix.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="additionalRank">Rank</Label>
                        <Input
                          id="additionalRank"
                          type="text"
                          placeholder="eg. ProvJGW / PPADoC / AGSec"
                          value={additionalRank}
                          onChange={(e) => setAdditionalRank(e.target.value)}
                          required={requireAffiliationInfo}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="unit">
                          {unitLabel}{' '}
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="unit"
                          type="text"
                          placeholder={unitLabel}
                          value={unitName}
                          onChange={(e) => setUnitName(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Meal Preferences */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Meal Preferences</h3>

                  {event.courses && event.courses.length > 0 ? (
                    // Show course-based selections
                    event.courses.map((course) => (
                      <div key={course.id} className="space-y-2">
                        <Label htmlFor={`course-${course.id}`}>{course.name} *</Label>
                        <Select
                          value={courseSelections[course.id] || ''}
                          onValueChange={(value) =>
                            setCourseSelections({
                              ...courseSelections,
                              [course.id]: value,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select your choice..." />
                          </SelectTrigger>
                          <SelectContent>
                            {course.options.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))
                  ) : (
                    // Fallback to legacy meal choice
                    <div className="space-y-2">
                      <Label htmlFor="mealChoice">Meal Choice *</Label>
                      <Select
                        value={mealChoice}
                        onValueChange={setMealChoice}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your meal..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Beef">Beef Wellington</SelectItem>
                          <SelectItem value="Salmon">Salmon Fillet</SelectItem>
                          <SelectItem value="Vegetarian">Vegetarian Option</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Dietary Requirements</Label>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {dietaryOptions.map((option) => (
                        <label key={option} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={dietary.includes(option)}
                            onChange={() => toggleDietary(option)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="allergies">Allergies & Special Requirements</Label>
                    <Textarea
                      id="allergies"
                      placeholder="Please list any allergies or special dietary requirements..."
                      rows={3}
                      value={allergies}
                      onChange={(e) => setAllergies(e.target.value)}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/dashboard')}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Update RSVP'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

